import React from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import '../css/404.css';

// Router-level error element: shown for unknown URLs (404) and for
// unexpected render/loader crashes. Deliberately dependency-free
// (no MUI, no language context) - when the app has just crashed,
// this page must not rely on anything that might have crashed with it.
function Error404() {
  const error = useRouteError();

  const isNotFound = isRouteErrorResponse(error) && error.status === 404;

  const technicalDetails = getTechnicalDetails(error);

  if (error && !isNotFound) {
    // surface the crash in the console for debugging / bug reports
    console.error('Unrecoverable route error:', error);
  }

  return (
    <main className="error-page">
      <div className="error-page__blob error-page__blob--one" />
      <div className="error-page__blob error-page__blob--two" />

      <section className="error-page__content">
        {isNotFound ? (
          <>
            <p className="error-page__code">404</p>
            <h1 className="error-page__title">This page doesn&apos;t exist</h1>
            <p className="error-page__text">
              The link may be outdated, or the address was mistyped. Nothing
              broke &mdash; the page just isn&apos;t here.
            </p>
          </>
        ) : (
          <>
            <div className="error-page__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="44" height="44" fill="none">
                <path
                  d="M12 3 2.5 19.5h19L12 3Z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 9.5v5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="16.9" r="1" fill="currentColor" />
              </svg>
            </div>
            <h1 className="error-page__title">Something unexpected happened</h1>
            <p className="error-page__text">
              The application ran into an error it couldn&apos;t recover from.
              This isn&apos;t something you did &mdash; it&apos;s a bug on our
              side. Reloading usually gets things back on track.
            </p>
          </>
        )}

        <div className="error-page__actions">
          {isNotFound ? (
            <a className="error-page__button" href="/">
              Take me home
            </a>
          ) : (
            <button
              type="button"
              className="error-page__button"
              onClick={() => window.location.reload()}
            >
              Reload this page
            </button>
          )}
          <button
            type="button"
            className="error-page__button error-page__button--ghost"
            onClick={() => window.history.back()}
          >
            Go back
          </button>
          {!isNotFound && (
            <a
              className="error-page__button error-page__button--ghost"
              href="/"
            >
              Home
            </a>
          )}
        </div>

        {technicalDetails && (
          <details className="error-page__details">
            <summary>Technical details</summary>
            <pre>{technicalDetails}</pre>
          </details>
        )}

        <p className="error-page__footnote">
          {isNotFound
            ? 'If a link inside the dashboard brought you here, we would love to know about it.'
            : 'If this keeps happening, please let us know what you were doing right before it - that makes the bug much easier to find.'}
        </p>
      </section>
    </main>
  );
}

function getTechnicalDetails(error) {
  if (!error) return '';
  try {
    if (isRouteErrorResponse(error)) {
      if (error.status === 404) return '';
      return `${error.status} ${error.statusText}\n${
        typeof error.data === 'string' ? error.data : ''
      }`.trim();
    }
    if (error instanceof Error) {
      return `${error.message}\n\n${error.stack ?? ''}`.trim();
    }
    return String(error);
  } catch {
    return '';
  }
}

export default Error404;
